import { Search } from '@mui/icons-material';
import {
    FormControl,
    InputAdornment,
    ListSubheader,
    MenuItem,
    Select,
    SelectChangeEvent,
    SxProps,
    TextField,
    Theme,
} from '@mui/material';
import type { FC, ReactNode } from 'react';
import { useMemo, useState } from 'react';

export interface SearchableSelectOption {
    value: string;
    label: string;
}

interface SearchableSelectProps {
    value: string | null;
    onChange: (value: string | null) => void;
    options: SearchableSelectOption[];
    placeholder: ReactNode;
    searchPlaceholder: string;
    sx?: SxProps<Theme>;
}

export const SearchableSelect: FC<SearchableSelectProps> = ({
    value,
    onChange,
    options,
    placeholder,
    searchPlaceholder,
    sx,
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredOptions = useMemo(
        () => options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase())),
        [options, searchTerm],
    );

    const handleChange = (event: SelectChangeEvent<string>) => {
        const newValue = event.target.value;
        onChange(newValue === '' ? null : newValue);
    };

    return (
        <FormControl size="small" sx={sx}>
            <Select
                value={value ?? ''}
                onChange={handleChange}
                displayEmpty
                sx={{ height: '36px' }}
                MenuProps={{
                    disableAutoFocusItem: true,
                    PaperProps: {
                        sx: {
                            maxHeight: 400,
                        },
                    },
                    MenuListProps: {
                        sx: {
                            paddingTop: 0,
                        },
                    },
                }}
            >
                <ListSubheader
                    sx={{
                        p: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        position: 'sticky',
                        top: 0,
                        backgroundColor: 'background.paper',
                        zIndex: 1,
                        lineHeight: 1,
                    }}
                >
                    <TextField
                        size="small"
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        fullWidth
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                height: '36px',
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </ListSubheader>
                {!searchTerm && (
                    <MenuItem value="">
                        <em>{placeholder}</em>
                    </MenuItem>
                )}
                {filteredOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};
